// src/components/common/Navbar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import ThemeToggle from './ThemeToggle';
import LoginModal from './LoginModal';
import { logoutUser, deleteAccount, getProfile, setShowLoginModal, syncUserFromStorage, updateUserName } from '../../redux/slices/authSlice';
import { 
  FaUser, 
  FaSignOutAlt, 
  FaReceipt, 
  FaChevronDown,
  FaMapMarkerAlt,
  FaPrescription,
  FaGift,
  FaQuestionCircle,
  FaLock,
  FaQrcode,
  FaPhone,
  FaDownload,
  FaUserCircle,
  FaTrash,
  FaExclamationTriangle,
  FaEdit,
  FaGooglePlay
} from 'react-icons/fa';

// ✅ Correct paths for your images
import logoWithText from '../../assets/images/Only Logo Text.png';
import logoOnly from '../../assets/images/Only Logo.png';

// Direct palette access
const palette = {
  orange: '#FD8A36',
  bgLightTop: '#FFF4E6',
  bgLightMain: '#FFFFFF',
  textLightDark: '#2E2218',
  borderLight: '#F5E6DA',
  bgDarkTop: '#1E1712',
  textDarkLight: '#F4EFEA',
  borderDark: '#2A2017',
};

// ✅ App Download URL
const APP_DOWNLOAD_URL = 'https://play.google.com/store/apps/details?id=com.siancetiffin.app';

// ✅ Helper function to get correct image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  if (imagePath.includes('uploads/')) {
    return `https://admin.thefoodies.in/public/${imagePath}`;
  }
  
  if (imagePath.startsWith('profile_images/')) {
    return `https://admin.thefoodies.in/public/uploads/${imagePath}`;
  }
  
  return `https://admin.thefoodies.in/public/uploads/profile_images/${imagePath}`;
};

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useSelector((state) => state.theme.mode);
  
  const { isAuthenticated, user, phone, showLoginModal } = useSelector((state) => state.auth);
  
  const isDark = theme === 'dark';
  
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const dropdownRef = useRef(null);
  const [forceUpdate, setForceUpdate] = useState(0);

  // ✅ Force refresh navbar
  const refreshNavbar = () => {
    console.log('🔄 Refreshing navbar...');
    setForceUpdate(prev => prev + 1);
  };

  // ✅ Get user data directly from localStorage
  const getUserDataFromStorage = () => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      console.log('📦 Navbar: Direct localStorage read:', storedUser);
      return storedUser;
    } catch (e) {
      console.error('❌ Error reading localStorage:', e);
      return null;
    }
  };

  // ✅ Get user data - try Redux first, fallback to localStorage
  const getUserData = () => {
    // First try Redux
    if (user && user.name) {
      console.log('✅ Navbar: Using Redux user:', user);
      return user;
    }
    
    // Then try localStorage directly
    const storedUser = getUserDataFromStorage();
    if (storedUser && storedUser.name) {
      console.log('✅ Navbar: Using localStorage user:', storedUser);
      return storedUser;
    }
    
    // Check auth login data
    const authUser = JSON.parse(localStorage.getItem('tiffinUserLogin') || '{}');
    if (authUser && authUser.name) {
      console.log('✅ Navbar: Using auth login user:', authUser);
      return authUser;
    }
    
    console.log('⚠️ Navbar: No user data found');
    return null;
  };

  // ✅ Check authentication and update on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = getUserDataFromStorage();
    
    console.log('📦 Navbar: Mount - localStorage user:', storedUser);
    console.log('📦 Navbar: Mount - Redux user:', user);
    console.log('📦 Navbar: Mount - Token exists:', !!token);
    
    // If token exists and we have a user in localStorage, sync it
    if (token && storedUser && storedUser.name) {
      if (!user || !user.name) {
        console.log('🔄 Syncing user from localStorage to Redux');
        dispatch(syncUserFromStorage()).then(() => {
          refreshNavbar();
        }).catch(() => {});
      }
    }
    
    // If token exists but no user in Redux, fetch profile
    if (token && !isAuthenticated) {
      console.log('🔄 Navbar: Fetching profile...');
      dispatch(getProfile()).then(() => {
        refreshNavbar();
      }).catch(() => {});
    }
    
    // Force refresh after mount
    setTimeout(refreshNavbar, 500);
  }, [dispatch, isAuthenticated, user]);

  // ✅ Listen for Redux user changes
  useEffect(() => {
    console.log('🔄 Navbar: Redux user changed:', user);
    if (user && user.name) {
      refreshNavbar();
    }
  }, [user]);

  // ✅ Listen for localStorage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'user' || e.key === 'profileCompleted' || e.key === 'tiffinUserLogin') {
        console.log('📦 Storage changed:', e.key, e.newValue);
        refreshNavbar();
        dispatch(syncUserFromStorage()).catch(() => {});
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [dispatch]);

  // ✅ Listen for custom profileUpdated event
  useEffect(() => {
    const handleProfileUpdate = (e) => {
      console.log('📢 Profile updated event received:', e.detail);
      refreshNavbar();
      dispatch(syncUserFromStorage()).catch(() => {});
    };
    
    window.addEventListener('profileUpdated', handleProfileUpdate);
    return () => window.removeEventListener('profileUpdated', handleProfileUpdate);
  }, [dispatch]);

  // ✅ Get display data - SHOW THE NAME EVEN IF IT'S "User"
  const userData = getUserData();
  
  // ✅ Display name - show the actual name from data
  const displayName = userData?.name || 'Account';
  const displayPhone = phone || userData?.phone || userData?.mobile || '';

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setDropdownOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ✅ Handle account button click
  const handleAccountClick = () => {
    const token = localStorage.getItem('token');
    const isAuth = isAuthenticated || !!token;
    
    if (!isAuth) {
      dispatch(setShowLoginModal(true));
    } else {
      setDropdownOpen(!dropdownOpen);
    }
  };

  // ✅ Updated Logout Handler
  const handleLogout = async () => {
    try {
      console.log('📤 Logging out...');
      await dispatch(logoutUser()).unwrap();
      setDropdownOpen(false);
      // ✅ Navigate to home page after logout
      navigate('/');
    } catch (error) {
      console.error('❌ Logout error:', error);
      // Even if API fails, clear local data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('isNewUser');
      localStorage.removeItem('profileCompleted');
      localStorage.removeItem('tiffinPhone');
      localStorage.removeItem('tiffinUserLogin');
      navigate('/');
    }
  };

  const handleDeleteAccount = () => {
    setDropdownOpen(false);
    navigate('/delete-account');
  };

  // ✅ Get profile image URL
  const getProfileImageUrl = () => {
    const profileImage = userData?.profile_image || userData?.avatar;
    if (!profileImage) return null;
    if (imageError) return null;
    return getImageUrl(profileImage);
  };

  const profileImageUrl = getProfileImageUrl();

  const navbarStyles = {
    light: {
      backgroundColor: palette.bgLightTop,
      borderBottom: `1px solid ${palette.borderLight}`,
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    },
    dark: {
      backgroundColor: palette.bgDarkTop,
      borderBottom: `1px solid ${palette.borderDark}`,
    }
  };

  const currentStyle = isDark ? navbarStyles.dark : navbarStyles.light;
  const textColor = isDark ? palette.textDarkLight : palette.textLightDark;

  const dropdownItems = [
    { icon: FaUser, label: 'My Account', path: '/profile' },
    { icon: FaReceipt, label: 'My Orders', path: '/orders' },
    { icon: FaMapMarkerAlt, label: 'Saved Addresses', path: '/addresses' },
    { icon: FaPrescription, label: 'My Prescriptions', path: '/prescriptions' },
    { icon: FaGift, label: 'E-Gift Cards', path: '/gift-cards' },
    { icon: FaQuestionCircle, label: "FAQ's", path: '/faq' },
    { icon: FaLock, label: 'Account Privacy', path: '/privacy' },
  ];

  return (
    <>
      <nav 
        className="sticky top-0 z-50 px-4 sm:px-6 lg:px-8 py-3 transition-colors duration-300"
        style={currentStyle}
        key={forceUpdate}
      >
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <div className="flex items-center gap-2">
              <img 
                src={logoOnly} 
                alt="Foodies Tiffin" 
                className="h-10 w-10 object-contain transition-transform duration-300 group-hover:scale-110"
                style={{ filter: isDark ? 'brightness(1.1)' : 'none' }}
              />
              <img 
                src={logoWithText} 
                alt="Foodies Tiffin" 
                className="h-8 object-contain transition-transform duration-300 group-hover:scale-105"
                style={{ filter: isDark ? 'brightness(1.1)' : 'none' }}
              />
            </div>
          </Link>

          {/* Navigation Actions */}
          <div className="flex items-center gap-3">
            {/* Account Button */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={handleAccountClick}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 hover:scale-105 ${
                  dropdownOpen || isAuthenticated
                    ? isDark ? 'bg-white/10' : 'bg-[#FD8A36]/10'
                    : isDark ? 'hover:bg-white/10' : 'hover:bg-[#FD8A36]/5'
                }`}
                style={{ 
                  color: dropdownOpen || isAuthenticated ? palette.orange : textColor,
                  border: dropdownOpen || isAuthenticated ? `1px solid ${palette.orange}` : '1px solid transparent'
                }}
              >
                {isAuthenticated ? (
                  <>
                    {profileImageUrl ? (
                      <img
                        src={profileImageUrl}
                        alt={displayName}
                        className="w-7 h-7 rounded-full object-cover border-2"
                        style={{ borderColor: palette.orange }}
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      <FaUserCircle className="text-xl" />
                    )}
                    <span className="text-sm font-medium hidden sm:inline">
                      {displayName}
                    </span>
                    <FaChevronDown 
                      className={`text-xs transition-transform duration-200 ${
                        dropdownOpen ? 'rotate-180' : ''
                      }`} 
                    />
                  </>
                ) : (
                  <>
                    <FaUser className="text-lg" />
                    <span className="text-sm font-medium hidden sm:inline">Sign In</span>
                  </>
                )}
              </button>

              {/* Dropdown */}
              {isAuthenticated && dropdownOpen && (
                <div 
                  className="absolute right-0 mt-2 w-72 rounded-xl shadow-lg py-3 border transition-all duration-200 max-h-[80vh] overflow-y-auto"
                  style={{ 
                    backgroundColor: isDark ? palette.bgDarkTop : '#FFFFFF',
                    borderColor: isDark ? palette.borderDark : palette.borderLight,
                    boxShadow: isDark 
                      ? '0 20px 60px rgba(0,0,0,0.5)' 
                      : '0 20px 60px rgba(0,0,0,0.12)'
                  }}
                >
                  {/* User Info */}
                  <div className="px-4 py-3 border-b" style={{ 
                    borderColor: isDark ? palette.borderDark : palette.borderLight 
                  }}>
                    <div className="flex items-center gap-3">
                      {profileImageUrl ? (
                        <img
                          src={profileImageUrl}
                          alt={displayName}
                          className="w-12 h-12 rounded-full object-cover border-2"
                          style={{ borderColor: palette.orange }}
                          onError={() => setImageError(true)}
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[#FD8A36]/20">
                          <FaUser className="text-xl" style={{ color: palette.orange }} />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-semibold" style={{ color: textColor }}>
                          {displayName}
                        </p>
                        {displayPhone && (
                          <p className="text-sm font-medium flex items-center gap-1" style={{ 
                            color: palette.orange 
                          }}>
                            <FaPhone className="text-xs" />
                            {displayPhone}
                          </p>
                        )}
                        <Link 
                          to="/profile" 
                          onClick={() => setDropdownOpen(false)}
                          className="text-xs flex items-center gap-1 mt-0.5 hover:underline"
                          style={{ color: palette.orange }}
                        >
                          <FaEdit className="text-[8px]" /> Edit Profile
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items */}
                  {dropdownItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.path}
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 transition-colors duration-150"
                      style={{ color: textColor }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(253,138,54,0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <item.icon className="text-sm" style={{ color: isDark ? '#8A7A6A' : '#6B5A4A' }} />
                      <span className="text-sm font-medium flex-1">{item.label}</span>
                      <span className="text-xs" style={{ color: isDark ? '#5A4A3A' : '#B0A090' }}>›</span>
                    </Link>
                  ))}

                  <div className="my-1 border-t" style={{ 
                    borderColor: isDark ? palette.borderDark : palette.borderLight 
                  }} />

                  {/* ✅ Download App - Updated with correct link */}
                  <div className="px-4 py-3" style={{ 
                    backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(253,138,54,0.04)',
                    margin: '0 8px',
                    borderRadius: '12px'
                  }}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                        backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(253,138,54,0.12)'
                      }}>
                        <FaQrcode className="text-xl" style={{ color: palette.orange }} />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-medium" style={{ color: textColor }}>
                          Simple way to get groceries at your doorstep
                        </p>
                        <a
                          href={APP_DOWNLOAD_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 mt-1 text-xs font-medium transition-colors hover:opacity-80"
                          style={{ color: palette.orange }}
                        >
                          <FaGooglePlay className="text-[10px]" />
                          Download the app
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Delete Account */}
                  <button
                    onClick={handleDeleteAccount}
                    className="w-full flex items-center gap-3 px-4 py-3 mt-1 transition-colors duration-150"
                    style={{ color: '#DC2626' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = isDark ? 'rgba(220,38,38,0.1)' : 'rgba(220,38,38,0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <FaTrash className="text-sm" />
                    <span className="text-sm font-medium flex-1 text-left">Delete Account</span>
                    <FaExclamationTriangle className="text-xs" />
                  </button>

                  {/* Logout */}
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 transition-colors duration-150"
                    style={{ color: '#EF4444' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = isDark ? 'rgba(239,68,68,0.1)' : 'rgba(239,68,68,0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <FaSignOutAlt className="text-sm" />
                    <span className="text-sm font-medium flex-1 text-left">Log Out</span>
                  </button>
                </div>
              )}
            </div>

            <ThemeToggle />
          </div>
        </div>
      </nav>

      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => dispatch(setShowLoginModal(false))} 
      />
    </>
  );
};

export default Navbar;